/**
 * React hook (`useSpeechToText`) owning one `CaptureController` for dictation.
 * It feeds the controller callbacks from the latest committed render, and on
 * unmount cancels an unfinished recording while preserving one the user
 * already stopped, whose audio is still being finalized.
 */
import { RecordingStatus } from '@littlebird/sdk';
import { useEffect, useRef, useState } from 'react';

import { playSound } from '@/features/sounds/sounds';

import { createCaptureController } from './capture-controller';

/** Adapt one capture controller to React state and stable recording controls */
export function useSpeechToText(onTranscript: (transcription: string) => void) {
  const [recordingStatus, setRecordingStatus] = useState(RecordingStatus.None);
  const previousStatus = useRef(RecordingStatus.None);
  const callbacks = {
    onTranscript,
    onFailure: () => void playSound('problem'),
    onEmptyTranscript: () => void playSound('nothing'),
    onStatusChange: (status: RecordingStatus) => {
      const wasRecording = previousStatus.current === RecordingStatus.Recording;
      previousStatus.current = status;
      if (status === RecordingStatus.Recording && !wasRecording)
        void playSound('listen-open');
      // A cancel skips Processing, so only a stop reaches the sending cue
      else if (wasRecording && status === RecordingStatus.Processing)
        void playSound('listen-close');
      setRecordingStatus(status);
    },
  };
  // Construction only creates an in-memory object; microphone access and the
  // speech-activity claim begin later on a user action
  const [controller] = useState(() => createCaptureController(callbacks));

  // The controller outlives the render that built it; re-point it at each
  // render's callbacks so a transcript arriving later lands in the newest
  // `onTranscript`, not one frozen at mount
  useEffect(() => {
    controller.setCallbacks(callbacks);
  });

  useEffect(() => {
    // Stop an active recording on unmount, but preserve one the user already finished
    return () => controller.cancelRecordingUnlessFinishing();
  }, [controller]);

  return {
    startRecording: controller.startRecording,
    stopRecording: controller.stopRecording,
    cancelRecording: controller.cancelRecording,
    isCapturing: controller.isCapturing,
    hasPendingTranscription: controller.hasPendingTranscription,
    recordingStatus,
    mediaStreamRef: controller.mediaStreamRef,
  };
}
